import { ChangeEventHandler, FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RadioButton } from '@ebsco-ui/ebsco-ui';

import { Dropdown, DropdownItem, FeatureFlagDisplay } from '@app/components';
import { Feature, SetState } from '@app/constants';

import { CopyType } from '../usePlaceHoldCopies';

const copyTypeRadioButtonName = 'copyTypeRadioButton';

interface PlaceHoldCopiesProps {
  copyType: CopyType;
  copies: DropdownItem[];
  selectedCopy: DropdownItem | null;
  shouldDisplayCopiesDropdown: boolean;
  setCopyType: SetState<CopyType>;
  setSelectedCopy: SetState<DropdownItem | null>;
}

export const PlaceHoldCopies: FC<PlaceHoldCopiesProps> = ({
  copyType,
  copies,
  shouldDisplayCopiesDropdown,
  selectedCopy,
  setCopyType,
  setSelectedCopy,
}) => {
  const { $t } = useIntl();

  const handleCopyTypeChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => {
    const selectedType = Number(value) as CopyType;

    setCopyType(selectedType);
    setSelectedCopy(
      selectedType === CopyType.specificVolume ? copies[0] : null
    );
  };

  const renderCopyTypeRadios = () => (
    <fieldset>
      <legend>
        <FormattedMessage
          id="placeHoldModal.iWantToGet"
          defaultMessage="I want to get:"
        />
      </legend>
      <RadioButton
        label={$t({
          id: 'placeHoldModal.firstAvailableCopy',
          defaultMessage: 'First available copy',
        })}
        name={copyTypeRadioButtonName}
        value={CopyType.firstAvailable}
        checked={CopyType.firstAvailable === copyType}
        onChange={handleCopyTypeChange}
      />
      <RadioButton
        label={$t({
          id: 'placeHoldModal.specificCopy',
          defaultMessage: 'A specific copy (volume)',
        })}
        name={copyTypeRadioButtonName}
        value={CopyType.specificVolume}
        checked={CopyType.specificVolume === copyType}
        onChange={handleCopyTypeChange}
      />
    </fieldset>
  );

  return shouldDisplayCopiesDropdown ? (
    <>
      <FeatureFlagDisplay
        flagToMatch={{ name: Feature.firstAvailableCopyPlaceHold }}
        renderOn={renderCopyTypeRadios}
      />
      {copyType === CopyType.specificVolume && (
        <Dropdown
          label={$t({
            id: 'placeHoldModal.chooseCopy',
            defaultMessage: 'Choose a copy',
          })}
          id="place-hold-choose-copy"
          selectedItem={selectedCopy as DropdownItem}
          items={copies}
          styleType="standard"
          onSelectedItemChange={({ selectedItem }) => {
            setSelectedCopy(selectedItem ?? null);
          }}
        />
      )}
    </>
  ) : null;
};
